import React, {useState, useRef} from 'react';
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Form,
} from 'reactstrap';
import serialize from 'form-serialize';
import useGetSearchInputSuggestion from 'modules/advanced-search/useGetSearchInputSuggestion';
import urlJoin from 'url-join';
import {useHistory} from 'react-router-dom';
import {removePrefix} from 'utils/string';
import {MDB_PREFIX} from 'configs';
import cx from 'classnames';
import styles from './AdvancedSearchInput.module.scss';
import MuiIcon from 'components/Icons/MuiIcon';

function withAdvancedSearchInput(Component) {
  return function WithSingleSearchInput(props) {
    const {defaultValue, onSubmit, verifyCaptcha, className, name, ...rest} =
      props;
    const formRef = useRef(null);
    const history = useHistory();

    const [keyword, setKeyword] = useState(defaultValue || '');
    const [hasSearched, setHasSearched] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const {
      suggestions,
      getSuggestion,
      triggerDebouncedSearch,
      clearSuggestions,
      isLoading: isSuggestionsLoading,
      isDebouncing,
      searchType,
      recordsCount,
    } = useGetSearchInputSuggestion(verifyCaptcha);

    function clearAllStates() {
      setKeyword('');
      setIsFocused(false);
      setHasSearched(false);
      clearSuggestions();
    }

    const onSuggestionsFetchRequested = async (request) => {
      const {value} = request;

      if (value) {
        setHasSearched(true);

        // Call the debounced function
        triggerDebouncedSearch(value);
      }
    };

    const onSuggestionsClearRequested = () => {
      clearSuggestions();
      setHasSearched(false);
    };

    const getSuggestionValue = (suggestion) => {
      return suggestion.company_name || suggestion.certification_number;
    };

    async function handleSubmit(e) {
      e.preventDefault();

      if (keyword) {
        const form = formRef.current;
        const data = serialize(form, {hash: true, empty: true});

        onSubmit(data);

        setIsFocused(false);
        document.activeElement.blur();
      }
    }

    const inputProps = {
      name,
      value: keyword,
      id: 'single-search-input',
      onChange: (event, {newValue, method}) => {
        setKeyword(newValue || '');

        if (!newValue) {
          clearSuggestions();
          setHasSearched(false);
        }
      },
      onFocus: async () => {
        setIsFocused(true);
        if (keyword) setHasSearched(true);
      },
      onBlur: () => {
        setIsFocused(false);
        setHasSearched(false);
      },
    };

    function handleSuggestionSelected(suggestion) {
      const isCert = !!suggestion.certification_number;

      setIsFocused(false);
      setKeyword('');

      if (isCert) {
        const {certification_number} = suggestion;

        history.push(urlJoin('/certification', certification_number));
      } else {
        const {company_id} = suggestion;

        history.push(
          urlJoin('/certified-entity', removePrefix(company_id, MDB_PREFIX.ce))
        );
      }
    }

    function renderInputGenerator(
      inputProps,
      isFocused,
      isMobile,
      searchButtonProps = {
        color: 'primary',
        className: cx(
          styles['single-search__input-group-search-text-span'],
          'px-3 py-1'
        ),
        children: (
          <>
            <MuiIcon
              name="search"
              className={
                styles['single-search__input-group-search-text-span--icon']
              }
            />{' '}
            Search
          </>
        ),
      }
    ) {
      const {className, name, ...rest} = inputProps;

      return (
        <InputGroup
          className={cx(
            styles['single-search__input-group'],
            isFocused && styles['single-search__input-group--focused'],
            isMobile && styles['single-search__input-group--focused--mobile']
          )}
        >
          <InputGroupAddon addonType="prepend" className="flex-grow-1">
            <Input
              name={name}
              placeholder="Enter company by name or certification number"
              className={cx(
                className,
                'border-0 px-sm-4',
                !keyword && styles['single-search__input--italic'],
                isFocused && styles['single-search__input--focused']
              )}
              {...rest}
            />
          </InputGroupAddon>

          {keyword && (
            <InputGroupAddon addonType="prepend">
              <InputGroupText className="bg-white border-0">
                <MuiIcon
                  name="clear"
                  className="cursor-pointer"
                  onClick={() => {
                    setKeyword('');
                    clearSuggestions();
                  }}
                />
              </InputGroupText>
            </InputGroupAddon>
          )}

          <InputGroupAddon addonType="append">
            <InputGroupText
              className={cx(
                'bg-white border-0 pr-sm-2',
                styles['single-search__input-group-search-text'],
                isFocused &&
                  styles['single-search__input-group-search-text--focused'],
                isMobile &&
                  styles[
                    'single-search__input-group-search-text--focused--mobile'
                  ]
              )}
            >
              <Button type="submit" {...searchButtonProps}>
                {searchButtonProps.children}
              </Button>
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
      );
    }

    const themeGenerator = (isFocused, isMobile, isLimitReached) => {
      return {
        input: styles['single-search__input'],
        suggestionsContainer: cx(
          styles['single-search__suggestions-container'],
          {
            [styles['single-search__suggestions-container--focused']]:
              isFocused,
            [styles['single-search__suggestions-container--focused--mobile']]:
              isMobile,
          }
        ),
        suggestionHighlighted:
          !isLimitReached && styles['single-search__suggestion-highlighted'],
        container: cx(
          styles['single-search__input-container'],
          isMobile && styles['single-search__input-container--mobile']
        ),
        suggestion: cx(
          styles['single-search__suggestions-item'],
          isLimitReached &&
            styles['single-search__suggestions-item--limit-reached']
        ),
        suggestionsList: styles['single-search__suggestions-list'],
      };
    };

    const isLoading = isSuggestionsLoading || isDebouncing;

    const stateProps = {
      searchType,
      suggestions,
      isLoading,
      keyword,
      hasSearched,
      isFocused,
      setKeyword,
      setHasSearched,
      setIsFocused,
      clearSuggestions,
      getSuggestion,
      clearAllStates,
      recordsCount,
    };

    const suggestionProps = {
      handleSuggestionSelected,
      onSuggestionsFetchRequested,
      onSuggestionsClearRequested,
      getSuggestionValue,
      themeGenerator,
      renderInputGenerator,
      inputProps,
    };

    return (
      <Form innerRef={formRef} onSubmit={handleSubmit} className={className}>
        <Component
          stateProps={stateProps}
          suggestionProps={suggestionProps}
          verifyCaptcha={verifyCaptcha}
          {...rest}
        />
      </Form>
    );
  };
}

export default withAdvancedSearchInput;
